import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Table,
  Select,
  Input,
  Button,
  Space,
  Dropdown,
  Checkbox,
  App,
  Spin,
  Modal,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  MailOutlined,
  MoreOutlined,
  FilterOutlined,
  RightOutlined,
  LeftOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { listWinners, sendClaimForm, getPrizeClaim } from '../../services/competitions';
import { exportToExcel } from '../../utils/excel';
import './WinnersClaimList.css';

// Badges de estado con estilos idénticos a la maqueta
const PROCESS_STATUS_MAP = {
  'Ganador Calculado': { className: 'status-pill ganador-calculado' },
  'Formulario Enviado': { className: 'status-pill formulario-enviado' },
  'En Validación': { className: 'status-pill en-validacion' },
  'Corrección Solicitada': { className: 'status-pill correccion-solicitada' },
  'Aprobado para Pago': { className: 'status-pill aprobado-pago' },
  Pagado: { className: 'status-pill pagado' },
};

// Íconos por defecto según competencia
const COMPETITION_ICONS = {
  'FUT Cup 2024': '🏆',
  'SuperLiga FUT 2024': '🛡️',
  'FUT Challenge 2024': '🥇',
  'FUT Master 2024': '⚽',
};

const ELIGIBLE_VIDEO_STATUSES = ['FINALIZED'];



const WinnersClaimList = () => {
  const { message } = App.useApp();
  const location = useLocation();
  const restoredFilters = location.state?.filters;

  const [loading, setLoading] = useState(false);
  const [allWinnersData, setAllWinnersData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Filtros aplicados
  const [selectedCompetition, setSelectedCompetition] = useState(restoredFilters?.selectedCompetition ?? 'Todas');
  const [selectedWinnersLimit, setSelectedWinnersLimit] = useState(restoredFilters?.selectedWinnersLimit ?? 'all');
  const [selectedStatus, setSelectedStatus] = useState(restoredFilters?.selectedStatus ?? 'Todos');
  const [searchParticipant, setSearchParticipant] = useState(restoredFilters?.searchParticipant ?? '');

  // Draft de búsqueda
  const [draftSearch, setDraftSearch] = useState(restoredFilters?.searchParticipant ?? '');

  // Paginación
  const [currentPage, setCurrentPage] = useState(restoredFilters?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(restoredFilters?.pageSize ?? 10);

  // Cargar competencias y ganadores usando métodos existentes
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Consultar ganadores usando el servicio existente listWinners()
      const winnersRes = await listWinners({ page: 1, pageSize: 100 }).catch(() => null);

      if (winnersRes?.items && Array.isArray(winnersRes.items) && winnersRes.items.length > 0) {
        const processedRows = [];

        winnersRes.items.forEach((comp) => {
          const compTitle = comp.description || comp.name || comp.folio || `Competencia ${comp.id}`;
          const compSubtitle = comp.modality?.name || comp.modality?.description || comp.period?.description || 'Edición Especial';
          const videos = (comp.videos || []).filter((v) => ELIGIBLE_VIDEO_STATUSES.includes(v.status));

          videos.forEach((vid, vIdx) => {
            const partName = vid.participant
              ? `${vid.participant.firstName || ''} ${vid.participant.lastName || ''}`.trim()
              : vid.playerName || `Participante ${vIdx + 1}`;
            const partEmail = vid.participant?.email || `participante${vIdx + 1}@email.com`;
            const posStr = `${vIdx + 1}°`;
            const dateStr = comp.endDate ? dayjs(comp.endDate).format('DD/MM/YYYY') : '15/04/2024';
            // Si en el futuro se agrega el campo de reclamo en la BD (ej. vid.claimStatus o comp.prizeClaimStatus), se tomará de ahí.
            // Por el momento, mientras se agrega el campo, se muestra por defecto 'Ganador Calculado'.
            const statusStr =
              vid.prizeClaim?.status ||
              vid.claimStatus ||
              comp.prizeClaimStatus ||
              'Ganador Calculado';

            processedRows.push({
              id: `winner-${comp.id}-${vid.id || vIdx}`,
              videoId: vid.id,
              competitionId: comp.id,
              competition: compTitle,
              edition: compSubtitle,
              icon: COMPETITION_ICONS[compTitle] || '🏆',
              participant: partName,
              email: partEmail,
              positionNum: vIdx + 1,
              position: posStr,
              endDate: dateStr,
              status: statusStr,
              bankName: vid.prizeClaim?.bankName || '',
              clabe: vid.prizeClaim?.clabe || '',
            });
          });
        });

        if (processedRows.length > 0) {
          setAllWinnersData(processedRows);
        }
      }
    } catch (error) {
      console.error('Error al consultar ganadores:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Manejar cambio de competencia
  const handleCompetitionChange = (value) => {
    setSelectedCompetition(value);
    setSelectedWinnersLimit('all');
  };

  // Calcular la cantidad de ganadores disponibles para la competencia seleccionada
  const currentCompWinnersCount = useMemo(() => {
    if (selectedCompetition === 'Todas') return 0;
    const compWinners = allWinnersData.filter(
      (w) => w.competition === selectedCompetition || String(w.competitionId) === String(selectedCompetition)
    );
    return compWinners.length;
  }, [allWinnersData, selectedCompetition]);

  // Opciones para el filtro de ganadores a mostrar
  const winnersLimitOptions = useMemo(() => {
    if (!currentCompWinnersCount) return [];
    const options = [{ value: 'all', label: `Todos (${currentCompWinnersCount})` }];
    const presets = [1, 3, 5, 10];
    presets.forEach((num) => {
      if (num < currentCompWinnersCount) {
        options.push({ value: num, label: `Top ${num}` });
      }
    });
    return options;
  }, [currentCompWinnersCount]);

  // Aplicar filtros en memoria
  const filteredWinners = useMemo(() => {
    return allWinnersData.filter((item) => {
      // Filtro de Competencia
      if (selectedCompetition !== 'Todas' && item.competition !== selectedCompetition && String(item.competitionId) !== String(selectedCompetition)) {
        return false;
      }
      // Filtro de Cantidad de Ganadores
      if (selectedCompetition === 'Todas') {
        if (item.positionNum > 3) return false;
      } else {
        if (selectedWinnersLimit !== 'all' && item.positionNum > Number(selectedWinnersLimit)) {
          return false;
        }
      }
      // Filtro de Estado
      if (selectedStatus !== 'Todos' && item.status !== selectedStatus) {
        return false;
      }
      // Filtro de Participante (nombre o correo)
      if (searchParticipant.trim() !== '') {
        const query = searchParticipant.toLowerCase();
        const matchesName = item.participant.toLowerCase().includes(query);
        const matchesEmail = item.email.toLowerCase().includes(query);
        if (!matchesName && !matchesEmail) return false;
      }
      return true;
    });
  }, [allWinnersData, selectedCompetition, selectedWinnersLimit, selectedStatus, searchParticipant]);

  // Manejar actualización y búsqueda
  const handleUpdate = () => {
    setSearchParticipant(draftSearch);
    loadData();
    message.success('Listado actualizado');
  };

  const handleClearFilters = () => {
    setSelectedCompetition('Todas');
    setSelectedWinnersLimit('all');
    setSelectedStatus('Todos');
    setSearchParticipant('');
    setDraftSearch('');
    setSelectedRowKeys([]);
  };

  // Modal de confirmación y estado de envío
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [sendingForm, setSendingForm] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Participantes seleccionados
  const selectedParticipants = useMemo(() => {
    return allWinnersData.filter((w) => selectedRowKeys.includes(w.id));
  }, [allWinnersData, selectedRowKeys]);

  // Abrir modal de confirmación
  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const approvedWinners = allWinnersData.filter((w) => w.status === 'Aprobado para Pago');

      if (!approvedWinners.length) {
        message.warning('No hay registros con estatus Aprobado para Pago');
        setExporting(false);
        return;
      }

      // Group by competition
      const competitionsMap = {};

      for (const winner of approvedWinners) {
        if (!competitionsMap[winner.competition]) {
          competitionsMap[winner.competition] = [];
        }
        
        // Fetch detailed data to get bankName and clabe if missing
        let bankName = winner.bankName || '';
        let clabe = winner.clabe || '';
        
        if (!bankName || !clabe) {
          try {
            const claimDetail = await getPrizeClaim(winner.videoId);
            if (claimDetail) {
               bankName = claimDetail.bankName || bankName;
               clabe = claimDetail.clabe || clabe;
            }
          } catch (e) {
            console.error('Error fetching claim detail for export', e);
          }
        }

        competitionsMap[winner.competition].push({
          participant: winner.participant,
          bankName,
          clabe,
        });
      }

      const excelData = [];

      Object.entries(competitionsMap).forEach(([competitionName, winners]) => {
        // Cabecero con el nombre de la competencia (en una sola fila, usando la primera columna)
        excelData.push({
          'Nombre del Usuario': competitionName,
          'CLABE': '',
          'Banco': '',
        });

        // Listado de usuarios
        winners.forEach((w) => {
          excelData.push({
            'Nombre del Usuario': w.participant,
            'CLABE': w.clabe,
            'Banco': w.bankName,
          });
        });

        // Fila vacía como separador
        excelData.push({
          'Nombre del Usuario': '',
          'CLABE': '',
          'Banco': '',
        });
      });

      exportToExcel(excelData, [], 'Pagos Aprobados', 'pagos_aprobados');
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      message.error('Ocurrió un error al exportar la información');
    } finally {
      setExporting(false);
    }
  };

  const handleOpenConfirmModal = () => {
    if (selectedRowKeys.length === 0) return;
    setIsConfirmModalOpen(true);
  };

  // Confirmar y enviar llamada al backend
  const handleConfirmSend = async () => {
    setSendingForm(true);
    try {
      const validParticipants = selectedParticipants.filter(p => p.status === 'Ganador Calculado');
      const payload = {
        winnerIds: validParticipants.map((p) => p.videoId).filter(Boolean),
        userEmails: validParticipants.map((p) => p.email).filter(Boolean),
      };

      const response = await sendClaimForm(payload);

      message.success(
        response?.message ||
          `Formulario de reclamo enviado exitosamente a ${selectedRowKeys.length} participante(s)`
      );
      setIsConfirmModalOpen(false);
      setSelectedRowKeys([]);
      loadData(); // Refrescar los datos para ver el nuevo estado
    } catch (error) {
      console.error('Error al enviar formulario de reclamo:', error);
      message.error(
        error?.response?.data?.message ||
          'No se pudo enviar el formulario para reclamar premio'
      );
    } finally {
      setSendingForm(false);
    }
  };

  const navigate = useNavigate();

  // Opciones del menú de acciones
  const getActionMenuItems = (record) => [
    {
      key: 'send-form',
      label: 'Enviar Formulario de Reclamo',
      onClick: () => {
        setSelectedRowKeys([record.id]);
        setIsConfirmModalOpen(true);
      },
      disabled: record.status !== 'Ganador Calculado',
    },
    {
      key: 'view-detail',
      label: 'Ver Detalle del Ganador',
      onClick: () => navigate(`/prize-claims/view/${record.id}`, {
        state: {
          filters: {
            selectedCompetition,
            selectedWinnersLimit,
            selectedStatus,
            searchParticipant,
            currentPage,
            pageSize,
          },
        },
      }),
      disabled: record.status === 'Ganador Calculado',
    },
    {
      key: 'change-status',
      label: 'Cambiar Estado del Proceso',
      onClick: () => message.info(`Cambiar estado de ${record.participant}`),
      disabled: record.status === 'Ganador Calculado',
    },
  ];

  // Configuración de la tabla
  const columns = [
    {
      title: 'Competencia',
      dataIndex: 'competition',
      key: 'competition',
      render: (text, record) => (
        <div className="competition-cell">
          <div className="competition-icon-box">{record.icon || '🏆'}</div>
          <div className="competition-info">
            <span className="competition-title">{record.competition}</span>
            <span className="competition-subtitle">{record.edition}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Participante',
      dataIndex: 'participant',
      key: 'participant',
      render: (text, record) => (
        <div className="participant-info">
          <span className="participant-name">{record.participant}</span>
          <span className="participant-email">{record.email}</span>
        </div>
      ),
    },
    {
      title: 'Posición',
      dataIndex: 'position',
      key: 'position',
      align: 'center',
      render: (pos) => <span className="position-badge">{pos}</span>,
    },
    {
      title: 'Fecha Finalización',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => <span className="end-date-text">{date}</span>,
    },
    {
      title: 'Estado del Proceso',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => {
        const config = PROCESS_STATUS_MAP[status] || { className: 'status-pill ganador-calculado' };
        return <span className={config.className}>{status}</span>;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={['click']}>
          <Button className="btn-action-more" icon={<MoreOutlined style={{ fontSize: 18, transform: 'rotate(90deg)' }} />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedKeys) => {
      setSelectedRowKeys(newSelectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.status !== 'Ganador Calculado',
    }),
  };

  // Opciones de competencias para el Select
  const competitionOptions = [
    { value: 'Todas', label: 'Todas' },
    ...Array.from(new Set(allWinnersData.map((w) => w.competition))).map((c) => ({
      value: c,
      label: c,
    })),
  ];

  // Opciones de estados para el Select
  const statusOptions = [
    { value: 'Todos', label: 'Todos' },
    ...Object.keys(PROCESS_STATUS_MAP).map((st) => ({
      value: st,
      label: st,
    })),
  ];

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredWinners.slice(start, start + pageSize);
  }, [filteredWinners, currentPage, pageSize]);

  return (
    <div className="winners-claim-container">
      {/* Subtítulo y Título */}
      <div className="winners-claim-breadcrumb">Gestión de Reclamo de Premios</div>
      <div className="winners-claim-title">Consulta de Ganadores para Inicio del Proceso de Reclamo</div>

      {/* Tarjeta de Filtros */}
      <div className="winners-claim-filters-card">
        <Space wrap size={24} align="end" style={{ width: '100%' }}>
          <div className="winners-claim-filter-item">
            <span className="winners-claim-filter-label">Competencia</span>
            <Select
              value={selectedCompetition}
              onChange={handleCompetitionChange}
              options={competitionOptions}
              style={{ width: 220, height: 38 }}
            />
          </div>

          {selectedCompetition !== 'Todas' && (
            <div className="winners-claim-filter-item">
              <span className="winners-claim-filter-label">Ganadores a mostrar</span>
              <Select
                value={selectedWinnersLimit}
                onChange={setSelectedWinnersLimit}
                options={winnersLimitOptions}
                style={{ width: 180, height: 38 }}
              />
            </div>
          )}

          <div className="winners-claim-filter-item">
            <span className="winners-claim-filter-label">Estado del Proceso</span>
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={statusOptions}
              style={{ width: 220, height: 38 }}
            />
          </div>

          <div className="winners-claim-filter-item" style={{ flexGrow: 1, maxWidth: 360 }}>
            <span className="winners-claim-filter-label">Buscar participante</span>
            <Input
              placeholder="Buscar por nombre o correo..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onPressEnter={handleUpdate}
              suffix={<SearchOutlined style={{ color: '#64748b' }} />}
              style={{ height: 38, borderRadius: 8 }}
            />
          </div>

          <Button
            className="btn-clear-filters"
            icon={<FilterOutlined />}
            onClick={handleClearFilters}
          >
            Limpiar filtros
          </Button>

          <Button
            className="btn-update-navy"
            icon={<ReloadOutlined />}
            onClick={handleUpdate}
            loading={loading}
          >
            Actualizar
          </Button>

          <Tooltip title="Exportar a Excel (Aprobados para pago)">
            <Button
              className="btn-add"
              icon={<FileExcelOutlined />}
              onClick={handleExportToExcel}
              loading={exporting}
              disabled={exporting}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Barra de Selección y Acción */}
      <div className="winners-claim-action-bar">
        <div className="winners-claim-selection-text">
          <Checkbox
            checked={selectedRowKeys.length > 0 && selectedRowKeys.length === filteredWinners.length}
            indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < filteredWinners.length}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRowKeys(filteredWinners.map((w) => w.id));
              } else {
                setSelectedRowKeys([]);
              }
            }}
          />
          <span>{selectedRowKeys.length} seleccionados de {filteredWinners.length} registros</span>
        </div>

        <Button
          className={`btn-send-form ${selectedRowKeys.length > 0 ? 'active' : ''}`}
          icon={<MailOutlined />}
          onClick={handleOpenConfirmModal}
          disabled={selectedRowKeys.length === 0}
        >
          Enviar Formulario para Reclamar Premio
        </Button>
      </div>

      {/* Tabla de Resultados */}
      <div className="winners-claim-table-card">
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={paginatedData}
            rowSelection={rowSelection}
            pagination={false}
          />
        </Spin>

        {/* Paginador Personalizado */}
        <div className="winners-claim-footer">
          <div className="footer-records-count">
            Mostrando {filteredWinners.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} a{' '}
            {Math.min(currentPage * pageSize, filteredWinners.length)} de {filteredWinners.length} registros
          </div>

          <div className="footer-pagination">
            <Space size={8}>
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                icon={<LeftOutlined style={{ fontSize: 12 }} />}
                style={{ borderRadius: 6 }}
              />
              {Array.from(
                { length: Math.ceil(filteredWinners.length / pageSize) || 1 },
                (_, i) => i + 1
              ).map((page) => (
                <Button
                  key={page}
                  type={page === currentPage ? 'primary' : 'default'}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    borderRadius: 6,
                    fontWeight: 600,
                    backgroundColor: page === currentPage ? '#0b192c' : '#ffffff',
                    borderColor: page === currentPage ? '#0b192c' : '#d9d9d9',
                  }}
                >
                  {page}
                </Button>
              ))}
              <Button
                disabled={currentPage >= Math.ceil(filteredWinners.length / pageSize)}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                icon={<RightOutlined style={{ fontSize: 12 }} />}
                style={{ borderRadius: 6 }}
              />
            </Space>

            <Space size={8} style={{ fontSize: 13, color: '#475569' }}>
              <span>Mostrar</span>
              <Select
                value={pageSize}
                onChange={(val) => {
                  setPageSize(val);
                  setCurrentPage(1);
                }}
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                ]}
                style={{ width: 70 }}
              />
              <span>por página</span>
            </Space>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación de Envío */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 22 }} />
            <span style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              Confirmar envío de formulario
            </span>
          </Space>
        }
        open={isConfirmModalOpen}
        onCancel={() => !sendingForm && setIsConfirmModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsConfirmModalOpen(false)}
            disabled={sendingForm}
            style={{ borderRadius: 8 }}
          >
            Cancelar
          </Button>,
          <Button
            key="confirm"
            type="primary"
            className="btn-update-navy"
            icon={<SendOutlined />}
            loading={sendingForm}
            onClick={handleConfirmSend}
            style={{ borderRadius: 8 }}
          >
            Confirmar y Enviar
          </Button>,
        ]}
        width={560}
      >
        <div style={{ padding: '12px 0' }}>
          <p style={{ fontSize: 14, color: '#334155', marginBottom: 16 }}>
            ¿Estás seguro de enviar el formulario para reclamar premio a los{' '}
            <strong>{selectedRowKeys.length} participante(s) seleccionado(s)</strong>?
          </p>

          <div
            style={{
              maxHeight: 220,
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '12px 16px',
              backgroundColor: '#f8fafc',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>
              Participantes a notificar ({selectedParticipants.length}):
            </div>
            {selectedParticipants.map((p, idx) => (
              <div
                key={p.id || idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: idx < selectedParticipants.length - 1 ? '1px solid #e2e8f0' : 'none',
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{p.participant}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{p.email}</div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', textAlign: 'right' }}>
                  <span>{p.competition}</span>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Posición {p.position}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WinnersClaimList;
